import { WbEditor } from '@agilelab/plugin-wb-platform';
import { Box, Typography, useTheme } from '@material-ui/core';
import { WidgetProps } from '@rjsf/utils';
import { debounce } from 'lodash';
import { useMemo } from 'react';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { isHidden } from '../../utils';
import { UseDependencyManagerResult } from '../DependencyManager';
import { extractFieldPathFromRjsfId } from '../utils/extractFieldPathFromRjsfId';

export const CodeBlockWidget = ({
  id,
  required,
  readonly,
  disabled,
  value,
  onChange,
  options,
  schema,
  uiSchema,
  rawErrors = [],
  formContext,
}: WidgetProps) => {
  const codeLanguage =
    (uiSchema?.['ui:options']?.language as string | undefined) ?? 'plaintext';
  const codeHeight =
    (uiSchema?.['ui:options']?.height as string | undefined) ?? '300px';
  const theme = useTheme();
  const { debounceMs } = useFormExtraConfigContext();

  const rootSchema = formContext?.rootSchema;
  const dependencyManager = formContext?.dependencyManager as
    | UseDependencyManagerResult
    | undefined;
  const fieldPath = useMemo(() => {
    return extractFieldPathFromRjsfId(id, rootSchema);
  }, [id, rootSchema]);
  const emptyValue = options.emptyValue;

  const debouncedEditorChange = useMemo(
    () =>
      debounce((v: string | undefined) => {
        const val = v === '' ? emptyValue : v ?? emptyValue;
        if (dependencyManager?.notifyUpdate && fieldPath) {
          dependencyManager.notifyUpdate({
            fieldPath,
            value: val,
            source: 'user',
            intent: 'manual',
          });
        } else {
          onChange(val);
        }
      }, debounceMs ?? 0),
    [onChange, dependencyManager, fieldPath, debounceMs, emptyValue],
  );

  const isDisabled = disabled || readonly;
  const hasErrors = rawErrors.length > 0;

  let borderColor: string;
  if (hasErrors) {
    borderColor = theme.palette.error.main;
  } else if (isDisabled) {
    borderColor = theme.palette.action.disabled;
  } else {
    borderColor = theme.palette.divider;
  }

  let titleColor: string;
  if (hasErrors) {
    titleColor = theme.palette.error.main;
  } else if (isDisabled) {
    titleColor = theme.palette.text.disabled;
  } else {
    titleColor = theme.palette.secondary.main;
  }

  return (
    <Box style={{ display: isHidden(uiSchema) ? 'none' : undefined }}>
      <Typography
        variant="body2"
        style={{
          fontWeight: 600,
          marginBottom: 4,
          color: titleColor,
        }}
      >
        {schema.title}
        {required && <span aria-hidden>{' *'}</span>}
      </Typography>
      <Box
        style={{
          border: `1px solid ${borderColor}`,
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: isDisabled
            ? theme.palette.action.disabledBackground
            : undefined,
          filter: isDisabled ? 'grayscale(1)' : undefined,
        }}
      >
        <WbEditor
          height={codeHeight}
          language={codeLanguage}
          value={value ?? ''}
          onChange={debouncedEditorChange}
          options={{
            readOnly: isDisabled,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            lineNumbers: 'off',
            glyphMargin: false,
            folding: false,
          }}
        />
      </Box>
    </Box>
  );
};
